import PropTypes from 'prop-types';
import React from 'react';

import {
  formatCurrency,
  formatDateTime,
  formatPercent,
  formatNumber,
} from '../../../shared/formattingUtils';

const sectionHeaderStyle = {
  background: "var(--od-c-f5f5f5, #333333)",
  padding: "8px 15px",
  marginTop: "20px",
};

const sectionHeaderTextStyle = {
  fontSize: "14px",
  color: "white",
  margin: "0",
  fontWeight: "bold",
};

const columnHeaderStyle = {
  fontWeight: "normal",
  color: "#B3B3B3",
  fontSize: "10px",
  textAlign: "left",
  paddingLeft: "15px",
  paddingTop: "8px",
  paddingBottom: "4px",
};

const columnHeaderRightStyle = {
  ...columnHeaderStyle,
  textAlign: "right",
  paddingRight: "15px",
};

const cellStyle = {
  padding: "5px 0 5px 15px",
  fontSize: "12px",
};

const cellRightStyle = {
  ...cellStyle,
  textAlign: "right",
  paddingRight: "15px",
};

const rowStyle = {
  background: "var(--od-c-f3f3f3, #f2f2f2)",
};

const labelCellStyle = {
  ...cellStyle,
  fontWeight: "600",
  width: "180px",
};

const valueCellStyle = {
  ...cellStyle,
};

const emptyStyle = {
  padding: "10px 15px",
  fontSize: "12px",
  color: "#999",
  fontStyle: "italic",
};

const SectionCard = ({ title, children }) => (
  <div style={{marginBottom: "4px"}}>
    <div style={sectionHeaderStyle}>
      <p style={sectionHeaderTextStyle}>{title}</p>
    </div>
    <div style={{marginTop: "2px"}}>
      {children}
    </div>
  </div>
);

const KeyValueRow = ({ label, value, isLast }) => (
  <tr style={rowStyle}>
    <td style={labelCellStyle}>{label}</td>
    <td style={valueCellStyle}>{value}</td>
  </tr>
);

const EventHeaderSection = ({ eventHeader }) => (
  <SectionCard title="Event Details">
    <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
      <colgroup>
        <col style={{width: "40%"}} />
        <col style={{width: "15%"}} />
        <col style={{width: "15%"}} />
        <col style={{width: "15%"}} />
        <col style={{width: "15%"}} />
      </colgroup>
      <thead>
        <tr>
          <th style={columnHeaderStyle}>TITLE</th>
          <th style={columnHeaderStyle}>VENUE</th>
          <th style={columnHeaderStyle}>AGE RESTRICTION</th>
          <th style={columnHeaderStyle}>VENUE CAPACITY</th>
          <th style={columnHeaderStyle}>EVENT ID</th>
        </tr>
      </thead>
      <tbody style={{fontSize: "12px"}}>
        <tr style={rowStyle}>
          <td style={cellStyle}>{eventHeader.title}</td>
          <td style={cellStyle}>{eventHeader.venue_name}</td>
          <td style={cellStyle}>{eventHeader.age_restriction || "None"}</td>
          <td style={cellStyle}>{formatNumber(eventHeader.venue_capacity)}</td>
          <td style={cellStyle}>{eventHeader.id}</td>
        </tr>
      </tbody>
    </table>
  </SectionCard>
);

const ArtistsSection = ({ artists }) => (
  <SectionCard title="Artists">
    {artists.length === 0 ? (
      <div style={emptyStyle}>No artists assigned.</div>
    ) : (
      <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
        <colgroup>
          <col style={{width: "40%"}} />
          <col style={{width: "15%"}} />
          <col style={{width: "15%"}} />
          <col style={{width: "15%"}} />
          <col style={{width: "15%"}} />
        </colgroup>
        <thead>
          <tr>
            <th style={columnHeaderStyle}>ARTIST</th>
            <th style={columnHeaderStyle}>TYPE</th>
            <th style={columnHeaderStyle}>SOFT MERCH SOLD BY</th>
            <th style={columnHeaderStyle}>SOFT MERCH RATE</th>
            <th style={columnHeaderStyle}>HARD MERCH RATE</th>
          </tr>
        </thead>
        <tbody style={{fontSize: "12px"}}>
          {artists.map((artist, index) => (
            <tr key={index} style={rowStyle}>
              <td style={cellStyle}>{artist.name}</td>
              <td style={cellStyle}>{artist.act_type}</td>
              <td style={cellStyle}>{artist.soft_merch_sold_by || "—"}</td>
              <td style={cellStyle}>{artist.soft_merch_rate != null ? formatPercent(artist.soft_merch_rate) : "—"}</td>
              <td style={cellStyle}>{artist.hard_merch_rate != null ? formatPercent(artist.hard_merch_rate) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </SectionCard>
);

const TicketsOverviewSection = ({ ticketsOverview }) => (
  <SectionCard title="Tickets Overview">
    <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
      <colgroup>
        <col style={{width: "16.66%"}} />
        <col style={{width: "16.66%"}} />
        <col style={{width: "16.66%"}} />
        <col style={{width: "16.66%"}} />
        <col style={{width: "16.66%"}} />
        <col style={{width: "16.66%"}} />
      </colgroup>
      <thead>
        <tr>
          <th style={columnHeaderStyle}>VENUE CAPACITY</th>
          <th style={columnHeaderStyle}>COMPS</th>
          <th style={columnHeaderStyle}>KILLS</th>
          <th style={columnHeaderStyle}>TOTAL SELLABLE</th>
          <th style={columnHeaderStyle}>FORECAST</th>
          <th style={columnHeaderStyle}>TICKETS SOLD</th>
        </tr>
      </thead>
      <tbody style={{fontSize: "12px"}}>
        <tr style={rowStyle}>
          <td style={cellStyle}>{formatNumber(ticketsOverview.venue_capacity)}</td>
          <td style={cellStyle}>{formatNumber(ticketsOverview.tickets_comped)}</td>
          <td style={cellStyle}>{formatNumber(ticketsOverview.tickets_killed)}</td>
          <td style={cellStyle}>{formatNumber(ticketsOverview.total_sellable)}</td>
          <td style={cellStyle}>{formatNumber(ticketsOverview.ticket_forecast)}</td>
          <td style={cellStyle}>{formatNumber(ticketsOverview.tickets_sold)}</td>
        </tr>
      </tbody>
    </table>
  </SectionCard>
);

const TicketScalingSection = ({ ticketScaling }) => (
  <SectionCard title="Ticket Scaling">
    {ticketScaling.length === 0 ? (
      <div style={emptyStyle}>No ticket types configured.</div>
    ) : (
      <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
        <thead>
          <tr>
            <th style={columnHeaderStyle}>TICKET TYPE</th>
            <th style={columnHeaderRightStyle}>AVAILABLE</th>
            <th style={columnHeaderRightStyle}>COMPS</th>
            <th style={columnHeaderRightStyle}>PRICE</th>
          </tr>
        </thead>
        <tbody style={{fontSize: "12px"}}>
          {ticketScaling.map((ticket, index) => (
            <tr key={index} style={rowStyle}>
              <td style={cellStyle}>{ticket.name}</td>
              <td style={cellRightStyle}>{formatNumber(ticket.available)}</td>
              <td style={cellRightStyle}>{formatNumber(ticket.comps)}</td>
              <td style={cellRightStyle}>{formatCurrency(ticket.price)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot style={{fontSize: "12px"}}>
          <tr style={{borderTop: "2px solid var(--od-c-333333, #333)"}}>
            <td style={{...cellStyle, fontWeight: "bold"}}>Total</td>
            <td style={{...cellRightStyle, fontWeight: "bold"}}>{formatNumber(ticketScaling.reduce((sum, t) => sum + (t.available || 0), 0))}</td>
            <td style={{...cellRightStyle, fontWeight: "bold"}}>{formatNumber(ticketScaling.reduce((sum, t) => sum + (t.comps || 0), 0))}</td>
            <td style={cellRightStyle}></td>
          </tr>
        </tfoot>
      </table>
    )}
  </SectionCard>
);

const ShowDetailsSection = ({ showDetails, timeZone }) => (
  <SectionCard title="Show Details">
    {showDetails.length === 0 ? (
      <div style={emptyStyle}>No show activities configured.</div>
    ) : (
      <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
        <thead>
          <tr>
            <th style={columnHeaderStyle}>ACTIVITY</th>
            <th style={columnHeaderRightStyle}>TIME</th>
          </tr>
        </thead>
        <tbody style={{fontSize: "12px"}}>
          {showDetails.map((activity, index) => (
            <tr key={index} style={rowStyle}>
              <td style={cellStyle}>{activity.name}</td>
              <td style={cellRightStyle}>{formatDateTime(activity.start_time, timeZone)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </SectionCard>
);

const ContactsSection = ({ contacts }) => (
  <SectionCard title="Contacts">
    {contacts.length === 0 ? (
      <div style={emptyStyle}>No contacts assigned.</div>
    ) : (
      <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
        <thead>
          <tr>
            <th style={columnHeaderStyle}>NAME</th>
            <th style={columnHeaderStyle}>TYPE</th>
            <th style={columnHeaderStyle}>EMAIL</th>
          </tr>
        </thead>
        <tbody style={{fontSize: "12px"}}>
          {contacts.map((contact, index) => (
            <tr key={index} style={rowStyle}>
              <td style={cellStyle}>{contact.name}</td>
              <td style={cellStyle}>{contact.contact_type || "—"}</td>
              <td style={cellStyle}>{contact.email || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </SectionCard>
);

const ConfirmationMemoReport = ({
  confirm,
  eventHeader,
  artists,
  ticketsOverview,
  ticketScaling,
  showDetails,
  contacts,
  timeZone,
}) => {
  return (
    <div className="confirmation-memo-report" style={{padding: "0px 7px", fontFamily: "Open Sans", fontSize: "14px"}}>
      <table style={{width: "100%"}}>
        <tbody>
          <tr>
            {confirm.venue_ownership && confirm.venue_ownership.logo_thumb_url ? (
              <td style={{textAlign: "left", verticalAlign: "middle"}}>
                <div style={{
                  width: "56px",
                  height: "56px",
                  overflow: "hidden",
                  borderRadius: "8px",
                  marginRight: "12px"
                }}>
                  <img src={confirm.venue_ownership.logo_thumb_url}
                        style={{height: "100%"}} />
                </div>
              </td>
            ) : null}
            <td style={{verticalAlign: "middle", width: "100%"}}>
              <p style={{margin: "0px", fontSize: "18px", textAlign: "left", display: "inline-block", float: "left"}}>
                <strong>CONFIRMATION MEMO</strong>
              </p>
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <p style={{margin: "0px", fontSize: "24px"}}>
                <strong>{confirm.title}</strong>
              </p>
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <p style={{margin: "-3px 0px 16px 0px", fontSize: "16px"}}>
                {`${confirm.human_start_date ? confirm.human_start_date : "TBD"} at ${confirm.venue.name}${confirm.venue.city_and_state ? ` in ${confirm.venue.city_and_state}` : ""}`}
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <EventHeaderSection eventHeader={eventHeader} />
      <ArtistsSection artists={artists} />
      <TicketsOverviewSection ticketsOverview={ticketsOverview} />
      <TicketScalingSection ticketScaling={ticketScaling} />
      <div style={{display: "flex", gap: "16px"}}>
        <div style={{flex: 1, minWidth: 0}}>
          <ShowDetailsSection showDetails={showDetails} timeZone={timeZone} />
        </div>
        <div style={{flex: 1, minWidth: 0}}>
          <ContactsSection contacts={contacts} />
        </div>
      </div>
    </div>
  );
};

ConfirmationMemoReport.propTypes = {
  confirm: PropTypes.object.isRequired,
  eventHeader: PropTypes.object.isRequired,
  artists: PropTypes.array.isRequired,
  ticketsOverview: PropTypes.object.isRequired,
  ticketScaling: PropTypes.array.isRequired,
  showDetails: PropTypes.array.isRequired,
  contacts: PropTypes.array.isRequired,
};

export default ConfirmationMemoReport;
